import * as React from "react";
import type { Schedule, ScheduleItem } from "src/types";

import { groupSchedulesWithTimezone } from "@/features/schedules/schedules-reducer";
import useEvent from "@/hooks/use-event";
import type { FilteredSchedule } from "@/hooks/use-schedule-filter";
import { useVenueType } from "@/hooks/use-venue-type";

/**
 * Custom hook that conditionally regroups and reformats schedule dates to use event timezone
 * for onsite/hybrid events, while keeping user timezone for virtual events.
 *
 * This is critical because schedule grouping by date must match the timezone used for display.
 * Without this, schedules could appear under the wrong date tabs.
 *
 * @param schedules - Array of filtered schedules from useScheduleFilter
 * @returns Schedules regrouped and formatted according to venue type
 */
export function useSchedulesWithEventTimezone(
  schedules: FilteredSchedule[]
): FilteredSchedule[] {
  const { event } = useEvent();
  const { venue_type } = useVenueType(); // This will fetch venue_type if needed

  return React.useMemo(() => {
    // Only apply timezone conversion for onsite or hybrid events
    const shouldUseEventTimezone =
      venue_type === "onsite" || venue_type === "hybrid";

    if (!shouldUseEventTimezone || !event?.standard_timezone) {
      return schedules;
    }

    // We need to regroup all schedule items using event timezone
    // First, collect all schedule items from all filtered schedules
    const allScheduleItems: ScheduleItem[] = schedules.flatMap(
      (schedule) => schedule.items
    );

    if (allScheduleItems.length === 0) {
      return schedules;
    }

    try {
      // Regroup using event timezone
      const regroupedSchedules = groupSchedulesWithTimezone(
        allScheduleItems,
        event.standard_timezone
      );

      // Convert back to FilteredSchedule format, preserving filter state
      return regroupedSchedules.map((schedule: Schedule) => ({
        items: schedule.schedules,
        isNoFilters: schedules[0]?.isNoFilters ?? true, // Use first schedule's filter state
        date: schedule.date,
        count: schedule.schedules.length,
      }));
    } catch (error) {
      // If there's an error, return the original schedules
      console.warn("Failed to regroup schedules with event timezone:", error);
      return schedules;
    }
  }, [schedules, event?.standard_timezone, venue_type]);
}
